// The repertoire piece's header shrinks its controls to fit beside the Back
// button. MUS-607: at their smallest (0.6) they needed about 610px, so in
// every window under about 700px wide the accuracy readout ran past the right
// edge (by 12px at 667x375), and in a phone held upright Performance Mode and
// Settings did too and could not be tapped. The controls now shrink a little
// further on one row (to 0.55, enough for 667px). Where even that does not
// fit:
// - in a window taller than 480px (a phone held upright) the header wraps its
//   controls onto a second row at 0.6;
// - in a shorter window (a small phone in landscape, 568x320) a second row
//   would cover the top of the score, so the header stays one row and drops
//   the dividers and the readout's ACCURACY label instead ("compact"), and
//   only then shrinks further, down to 0.45.
// It goes back to the full one-row header once the window has room for the
// width that header needed.
export const REPERTOIRE_HEADER_MIN_SCALE = 0.55;
export const REPERTOIRE_HEADER_WRAPPED_SCALE = 0.6;
export const REPERTOIRE_HEADER_COMPACT_MIN_SCALE = 0.45;
export const REPERTOIRE_HEADER_TWO_ROWS_MIN_HEIGHT_PX = 481;

export type RepertoireHeaderFit = {
  scale: number;
  wrapped: boolean;
  compact: boolean;
  // The width the full one-row controls needed when the header wrapped or
  // went compact.
  oneRowWidthPx: number;
};

export const REPERTOIRE_HEADER_ONE_ROW: RepertoireHeaderFit = {
  scale: 1,
  wrapped: false,
  compact: false,
  oneRowWidthPx: 0,
};

const SCALE_STEP = 0.02;

const clamp = (scale: number, min: number) => Math.max(min, Math.min(1, scale));

export const repertoireHeaderHasRoomForTwoRows = (windowHeightPx: number) =>
  windowHeightPx >= REPERTOIRE_HEADER_TWO_ROWS_MIN_HEIGHT_PX;

const fullRow = (): RepertoireHeaderFit => ({
  scale: REPERTOIRE_HEADER_MIN_SCALE,
  wrapped: false,
  compact: false,
  oneRowWidthPx: 0,
});

const wrappedFor = (oneRowWidthPx: number): RepertoireHeaderFit => ({
  scale: REPERTOIRE_HEADER_WRAPPED_SCALE,
  wrapped: true,
  compact: false,
  oneRowWidthPx,
});

const compactFor = (oneRowWidthPx: number): RepertoireHeaderFit => ({
  scale: REPERTOIRE_HEADER_MIN_SCALE,
  wrapped: false,
  compact: true,
  oneRowWidthPx,
});

// Recover the unscaled width by normalizing out the current scale, so the
// computation converges to the largest scale that still fits. Some controls
// keep their size, so the ratio can settle while the row is still past the
// screen edge: then step down at least one step.
const rescale = (
  current: RepertoireHeaderFit,
  availablePx: number,
  controlsWidthPx: number,
  controlsPastEdge: boolean,
  min: number,
): RepertoireHeaderFit => {
  const fitted = clamp(availablePx / (controlsWidthPx / current.scale), min);
  if (controlsPastEdge) {
    const scale = clamp(Math.min(fitted, current.scale - SCALE_STEP), min);
    return scale < current.scale ? { ...current, scale } : current;
  }
  return Math.abs(fitted - current.scale) > SCALE_STEP ? { ...current, scale: fitted } : current;
};

export const nextRepertoireHeaderFit = (
  current: RepertoireHeaderFit,
  availablePx: number,
  controlsWidthPx: number,
  controlsPastEdge: boolean,
  roomForTwoRows: boolean,
): RepertoireHeaderFit => {
  if (availablePx <= 0 || controlsWidthPx <= 0) return current;
  if (current.wrapped || current.compact) {
    if (availablePx >= current.oneRowWidthPx) return fullRow();
    if (current.wrapped && !roomForTwoRows) return compactFor(current.oneRowWidthPx);
    if (current.compact && roomForTwoRows) return wrappedFor(current.oneRowWidthPx);
    if (current.wrapped) return current;
    return rescale(
      current,
      availablePx,
      controlsWidthPx,
      controlsPastEdge,
      REPERTOIRE_HEADER_COMPACT_MIN_SCALE,
    );
  }
  if (controlsPastEdge && current.scale <= REPERTOIRE_HEADER_MIN_SCALE + 0.001) {
    return roomForTwoRows ? wrappedFor(controlsWidthPx) : compactFor(controlsWidthPx);
  }
  return rescale(
    current,
    availablePx,
    controlsWidthPx,
    controlsPastEdge,
    REPERTOIRE_HEADER_MIN_SCALE,
  );
};

// The header's height on one row: 70px in a window up to 960px wide, 110px
// above that.
export const repertoireHeaderOneRowHeightPx = (narrowWindow: boolean) => (narrowWindow ? 70 : 110);

// What the score already keeps clear above its title on a tablet or desktop
// (OSMDPlayableScene's vertical layout): the 110px toolbar plus 2px, and a
// 48px gap. A phone's score (the horizontal layout) keeps nothing.
export const REPERTOIRE_SCENE_TOOLBAR_RESERVE_PX = 112;
export const REPERTOIRE_SCENE_TITLE_MARGIN_PX = 48;

type WrappedHeader = {
  wrapped: boolean;
  headerHeightPx: number;
  narrowWindow: boolean;
  phoneLayout: boolean;
};

// How much lower the score must start so a wrapped header does not cover it.
// MUS-607: at 320x568 on a phone the header wraps to three rows (159px) and
// covered the first staff by 38px; there the score moves down by the header's
// height beyond one row. On a tablet or desktop the score already keeps 160px
// clear, so it moves only by what a taller header would take beyond that.
// A one-row header never moves it.
export const repertoireScoreExtraTopPx = ({
  wrapped,
  headerHeightPx,
  narrowWindow,
  phoneLayout,
}: WrappedHeader): number => {
  if (!wrapped) return 0;
  const alreadyClear = phoneLayout
    ? repertoireHeaderOneRowHeightPx(narrowWindow)
    : REPERTOIRE_SCENE_TOOLBAR_RESERVE_PX + REPERTOIRE_SCENE_TITLE_MARGIN_PX;
  return Math.max(0, headerHeightPx - alreadyClear);
};

// The space above the score while the header shows: the 35px it always was,
// plus the extra above.
export const REPERTOIRE_SCORE_TOP_SPACER_PX = 35;

export const repertoireScoreTopSpacerHeight = (headerVisible: boolean, headerExtraPx: number) =>
  headerVisible ? `${REPERTOIRE_SCORE_TOP_SPACER_PX + Math.max(0, headerExtraPx)}px` : '0px';
